import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextField } from 'components/Field/TextField';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { randomKey } from 'lib/riskbase';
import { intersection, isEqual, sortBy } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  eventSelectScreenSelection,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import {
  selectTasksAssignedFilterOrganisations,
  selectTasksAssignedFilterProperties,
  selectTasksAssignedSearch,
  selectTasksAssignedDraftFilterOptions,
  selectTasksAssignedFilterOptions,
} from 'store/tasks/selectors';
import {
  doTasksAssignedClearDraftFilterOptions,
  doTasksAssignedFilterOptionsLoad,
  doTasksAssignedLoad,
} from 'store/tasks/actions';
import { TASK_SEARCH_MAX_LENGTH } from '@riskbase/types';

type FilterSelectOption = {
  name: string;
  internalValue: string;
};

const getFilterLabel = (
  label: string,
  selectedCount: number,
  totalCount: number,
) =>
  `${label} (${selectedCount ? `${selectedCount} / ${totalCount}` : totalCount})`;

const getSelectedOptionNames = (
  options: FilterSelectOption[],
  selectedValues: string[],
) =>
  options
    .filter((option) => selectedValues.includes(option.internalValue))
    .map((option) => option.name)
    .join(', ');

export const TasksAssignedFilterModal: React.FC<
  ModalScreenProps<ModalScreens.TasksAssignedFilter>
> = ({ navigation }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const organisationSelectKeyRef = React.useRef(randomKey());
  const propertySelectKeyRef = React.useRef(randomKey());

  // Select filter options
  const draftFilterOptions = useSelector(selectTasksAssignedDraftFilterOptions);
  const filterOptions = useSelector(selectTasksAssignedFilterOptions);

  const organisationOptions =
    draftFilterOptions?.organisations ?? filterOptions.organisations;
  const propertyOptions =
    draftFilterOptions?.properties ?? filterOptions.properties;
  const organisationRequired =
    draftFilterOptions?.organisationRequired ??
    filterOptions.organisationRequired;

  // Select filter values
  const currentOrganisations = useSelector(
    selectTasksAssignedFilterOrganisations,
  );
  const currentProperties = useSelector(selectTasksAssignedFilterProperties);
  const currentSearch = useSelector(selectTasksAssignedSearch);

  const [organisations, setOrganisations] =
    React.useState(currentOrganisations);
  const [properties, setProperties] = React.useState(currentProperties);
  const [search, setSearch] = React.useState(currentSearch);

  const isDirty =
    !isEqual(organisations, currentOrganisations) ||
    !isEqual(properties, currentProperties) ||
    search !== currentSearch;

  const sortedOrganisationOptions = React.useMemo(
    (): FilterSelectOption[] =>
      sortBy(organisationOptions, 'name').map((organisation) => ({
        name: organisation.name,
        internalValue: organisation.id,
      })),
    [organisationOptions],
  );

  const sortedPropertyOptions = React.useMemo(
    (): FilterSelectOption[] =>
      sortBy(propertyOptions, 'name').map((property) => ({
        name: property.name,
        internalValue: property.id,
      })),
    [propertyOptions],
  );

  const handleCloseWithoutSaving = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);

  const handleApply = React.useCallback(() => {
    dispatch(
      doTasksAssignedLoad({
        filter: {
          organisations,
          properties,
          search: search.trim().slice(0, TASK_SEARCH_MAX_LENGTH),
        },
        showBlockingLoader: true,
      }),
    );
  }, [dispatch, organisations, properties, search]);

  const handleClose = React.useCallback(() => {
    if (isDirty) {
      confirmUnsaved({
        onSave: handleApply,
        onDiscard: handleCloseWithoutSaving,
      });
      return;
    }
    handleCloseWithoutSaving();
  }, [confirmUnsaved, handleApply, handleCloseWithoutSaving, isDirty]);

  const handleClear = React.useCallback(() => {
    setOrganisations([]);
    setProperties([]);
    setSearch('');
    dispatch(
      doTasksAssignedFilterOptionsLoad({
        showError: true,
        showBlockingLoader: true,
        draft: true,
      }),
    );
  }, [dispatch]);

  const handleOrganisationsChange = React.useCallback(
    (nextOrganisations: string[]) => {
      if (isEqual(nextOrganisations, organisations)) {
        return;
      }
      /**
       * There's potential for a user to have bad state here:
       * - User chooses "Fox Residential" organisation
       * - `setOrganisations` called; organisation field now says Fox Residential
       * - `doTasksAssignedFilterOptionsLoad` called but HTTP request fails
       * - Filter options were not loaded for Fox Residential but the organisation field says Fox Residential
       * Consider only setting `setOrganisation` after `doTasksAssignedFilterOptionsLoad` succeeded
       */
      setOrganisations(nextOrganisations);
      dispatch(
        doTasksAssignedFilterOptionsLoad({
          organisationIds: nextOrganisations.length
            ? nextOrganisations
            : undefined,
          showError: true,
          showBlockingLoader: true,
          draft: true,
        }),
      );
    },
    [dispatch, organisations],
  );

  const handleOpenOrganisationsSelect = React.useCallback(() => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        size: 'small',
        params: {
          title: 'Organisations',
          text: 'Changing the selected organisation may alter which other filters are available',
          searchable: true,
          eventKey: organisationSelectKeyRef.current,
          multiSelect: true,
          options: sortedOrganisationOptions,
          value: organisations,
          showQuickChoose: true,
        },
      }),
    );
  }, [dispatch, organisations, sortedOrganisationOptions]);

  const handleOpenPropertiesSelect = React.useCallback(() => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        size: 'small',
        params: {
          title: 'Properties',
          searchable: true,
          enableSelectAll: true,
          eventKey: propertySelectKeyRef.current,
          multiSelect: true,
          options: sortedPropertyOptions,
          value: properties,
          showQuickChoose: true,
        },
      }),
    );
  }, [dispatch, properties, sortedPropertyOptions]);

  // Listen for selections from the Select modal and route them back to
  // the task filter that opened it
  React.useEffect(() => {
    const events = [
      doAddEvent(eventSelectScreenSelection, (event) => {
        if (event.payload.key === organisationSelectKeyRef.current) {
          handleOrganisationsChange(event.payload.stringValues);
        } else if (event.payload.key === propertySelectKeyRef.current) {
          setProperties(event.payload.stringValues);
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [handleOrganisationsChange]);

  const organisationsBadgeActive = organisations.length !== 0;
  const propertiesBadgeActive = properties.length !== 0;
  const searchBadgeActive = search.trim().length !== 0;

  const isDefaultSelection =
    !organisations.length && !properties.length && !searchBadgeActive;

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty });
  }, [isDirty, navigation]);
  useEsc(handleClose);

  // Called when options change and ensures chosen values are still valid
  React.useEffect(() => {
    setProperties((currentValue) =>
      intersection(
        currentValue,
        propertyOptions.map((o) => o.id),
      ),
    );
  }, [propertyOptions]);

  // Use effect called on component first load and final unload
  React.useEffect(() => {
    return () => {
      // Clear draft filter options on filter close
      dispatch(doTasksAssignedClearDraftFilterOptions());
    };
  }, [dispatch]);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Filter"
          subtitle="Tasks"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleApply}
        />
      }
    >
      <Card
        explanation={
          organisationRequired
            ? 'Choose at least one organisation to see the available filters'
            : 'Changing the organisations may affect which other filters are available to you below'
        }
      >
        <CardRow
          label={getFilterLabel(
            'Organisations',
            organisations.length,
            sortedOrganisationOptions.length,
          )}
          value={getSelectedOptionNames(
            sortedOrganisationOptions,
            organisations,
          )}
          placeholder="All Organisations"
          labelAlignTop
          labelNumberOfLines={0}
          onPress={handleOpenOrganisationsSelect}
          actionIcon={ICONS.CircleXmarkLight}
          onAction={
            organisations.length
              ? () => handleOrganisationsChange([])
              : undefined
          }
          showBadge={organisationsBadgeActive}
        />
      </Card>

      {!organisationRequired && (
        <Card>
          <TextField
            iconLeft={ICONS.MagnifyingGlassLight}
            value={search}
            onChange={setSearch}
            placeholder="Search"
            onEsc={handleClose}
            maxLength={TASK_SEARCH_MAX_LENGTH}
            clearable
            autoCorrect={false}
            autoComplete="off"
            autoCapitalize="none"
          />
        </Card>
      )}

      {!organisationRequired && (
        <Card>
          <CardRow
            label={getFilterLabel(
              'Properties',
              properties.length,
              sortedPropertyOptions.length,
            )}
            value={getSelectedOptionNames(sortedPropertyOptions, properties)}
            placeholder="All Properties"
            labelAlignTop
            labelNumberOfLines={0}
            onPress={handleOpenPropertiesSelect}
            actionIcon={ICONS.CircleXmarkLight}
            onAction={properties.length ? () => setProperties([]) : undefined}
            showBadge={propertiesBadgeActive}
          />
        </Card>
      )}

      <Button
        text="Clear Filters"
        style={{
          alignSelf: 'center',
        }}
        onPress={handleClear}
        disabled={isDefaultSelection}
      />
    </Page>
  );
};
